import {useId, useMemo} from 'react';

import {ButtonRaw} from '@/components/Button';
import {ArrowRightIcon} from '@/components/Icons';
import AppImage from '@/components/Image/AppImage';
import useConfigExternalLink from '@/hooks/externalLink/useConfigExternalLink';
import {formatBadgeImageUrl, getInProgressBadgeImage} from '@/services/loyalty/badgeUtils';
import * as loyaltyUtils from '@/services/loyalty/loyaltyUtils';
import stringUtils from '@/utils/stringUtils';

import BadgeProgress from './BadgeProgress';

const BADGE_PROGRESS_BADGE_IMAGE_SIZE = 160;

const BadgeProgressContainer = ({
    journey,
    actionRemainingTitle,
    levelRemainingTitle,
    levelsRemainingTitle,
    actionsRemainingTitle,
    externalLink,
}) => {
    const {currentLevelBadge, progressCompleted, progressTotal, isSingleLevel} = useMemo(() => {
        return loyaltyUtils.getJourneyInfo(journey);
    }, [journey]);
    const {badgeId, journeyId, journeyName} = currentLevelBadge || {};
    const {redirectToExternalLink: redirect} = useConfigExternalLink({...externalLink, ssoParams: {journeyId}});
    const badgeTitleId = useId();
    const badgeDescriptionId = useId();
    const progressLeft = progressTotal - progressCompleted;
    const remainingTitle =
        progressLeft > 1
            ? isSingleLevel
                ? actionsRemainingTitle
                : levelsRemainingTitle
            : isSingleLevel
              ? actionRemainingTitle
              : levelRemainingTitle;
    const actionRemainingText = stringUtils.formatString(remainingTitle, progressLeft);
    const formattedBadgeImageUrl = formatBadgeImageUrl(
        getInProgressBadgeImage(currentLevelBadge)?.imageURL,
        BADGE_PROGRESS_BADGE_IMAGE_SIZE,
        BADGE_PROGRESS_BADGE_IMAGE_SIZE
    );

    const onBadgeButtonClick = () => {
        loyaltyUtils.trackLoyaltyClick(badgeId);
        redirect?.();
    };

    return (
        <ButtonRaw
            className='flex w-full flex-col overflow-clip rounded-none bg-primary-dark-5 md:bg-romance ml:rounded-xl'
            aria-describedby={badgeDescriptionId}
            onClick={onBadgeButtonClick}
        >
            <div className='flex w-full flex-row items-center justify-between pr-4'>
                <div className='flex flex-row'>
                    <AppImage
                        src={formattedBadgeImageUrl}
                        className='relative my-auto h-20 w-20 shrink-0'
                        decode={false}
                    />
                    <div className='gap-0.5 px-2 py-4 text-start'>
                        <h3 id={badgeTitleId} className='m-0 text-20 font-bold'>
                            {journeyName}
                        </h3>
                        <p id={badgeDescriptionId} className='text-16 font-normal text-primary-darker'>
                            {actionRemainingText}
                        </p>
                    </div>
                </div>
                <ArrowRightIcon />
            </div>
            <BadgeProgress stepsCount={progressTotal} finishedStepsCount={progressCompleted} />
        </ButtonRaw>
    );
};

export default BadgeProgressContainer;
